import Divider from "@components/shared/Divider";
import { PermissionGate } from "@components/shared/PermissionGate";
import { Tooltip } from "@components/ui/arrowed-tooltip";
import { Button } from "@components/ui/button";
import { AddScreenProvider, useAddScreen } from "@hooks/useAddScreen";
import { useAuth } from "@hooks/useAuth";
import { ScreenProvider } from "@hooks/useScreen";
import { ScreenWithPlayingInfo } from "@hooks/useScreens";
import { Screen } from "@hooks/useScreens/types";
import { env } from "@src/env.mjs";
import { useEffect, useState } from "react";
import { FiChrome, FiMonitor } from "react-icons/fi";
import { MdArrowBack } from "react-icons/md";
import CustomCarousel from "./CustomCarousel";
import ScreenInfoPage from "./ScreenInfoPage";
import ScreenPairPage from "./ScreenPairPage";

export interface AddScreenDialogProps {
  screen?: Screen;
  org_id: string;
  isFirstScreen?: boolean;
  onSuccess: (screen: ScreenWithPlayingInfo) => void;
  isPairAgain?: boolean;
}
export default function AddScreenDialog(props: AddScreenDialogProps) {
  return (
    <PermissionGate has="screens:create">
      <ScreenProvider screen={props.screen}>
        <AddScreenProvider props={props}>
          <AddScreenDialogPresenter {...props} />
        </AddScreenProvider>
      </ScreenProvider>
    </PermissionGate>
  );
}

function AddScreenDialogPresenter(props: AddScreenDialogProps) {
  const {
    inputPin,
    setInputPin,
    screenInfo,
    setScreenInfo,
    screenCreateStatus,
    onSubmit,
    updateScreen,
    onPairLater,
  } = useAddScreen();

  useEffect(() => {
    if (inputPin && screenCreateStatus !== "fetching") {
      onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputPin]);

  const [selectedPage, setSelectedPage] = useState<number>(
    props.isPairAgain ? 1 : 0,
  );

  const [showInitialScreenOptions, setShowInitialScreenOptions] = useState(
    props.isFirstScreen,
  );
  const [showBrowserScreenOptions, setShowBrowserScreenOptions] = useState(false);
  const { user } = useAuth();

  return (
    <div className="flex h-full w-full flex-col items-center justify-center text-center">
      <h1 className="mb-4 sm:mb-8 text-xl sm:text-2xl font-semibold leading-6 text-gray-900 dark:text-gray-100">
        {selectedPage === 1
          ? "Pair Screen"
          : props.screen
            ? "Edit Screen"
            : props.isFirstScreen
              ? "Let's add your first screen"
              : "Add Screen"}
      </h1>
      <div
        className="w-full max-w-2xl flex-1"
        style={{
          minHeight: props.screen && selectedPage === 0 ? "100px" : "",
        }}
      >
        {
          showBrowserScreenOptions ? (
            <ScreenPairPage
              key={"pair_page"}
              onPinChange={setInputPin}
              screenInfo={screenInfo}
              onSubmit={onSubmit}
              screenCreateStatus={screenCreateStatus}
              setSelectedPage={setSelectedPage}
              onPairLater={onPairLater}
              isPairAgain
              actions={
                <Button variant="outline" onClick={() => setShowBrowserScreenOptions(false)} className="gap-2">
                  <MdArrowBack className="size-4" />
                  Back
                </Button>
              }
            />
          ) :
            showInitialScreenOptions ? (
              <div className="flex h-full w-full flex-col items-center justify-center gap-4 sm:gap-6">
                <div className="mb-2 sm:mb-4 text-lg sm:text-xl text-center text-muted-foreground">
                  Choose how you'd like to set up your digital signage screen
                </div>
                <Button
                  variant="filled"
                  size="lg"
                  className="w-full max-w-md gap-2 sm:gap-3 py-4 sm:py-6 text-base sm:text-lg"
                  onClick={() => {
                    setShowInitialScreenOptions(false);
                  }}
                >
                  <FiMonitor className="h-5 w-5 sm:h-6 sm:w-6" />
                  <span>I have a TV screen ready to pair</span>
                </Button>
                <Divider message="OR" className="w-full max-w-md" />
                <UseBrowserButton
                  className="w-full max-w-md gap-2 sm:gap-3 py-4 sm:py-6 text-base sm:text-lg"
                  onClick={() => {
                    setScreenInfo({
                      name: "My Browser Screen",
                      screenType: "webBrowser",
                      deviceId: user?.id,
                    })
                    setShowBrowserScreenOptions(true);
                  }}
                />
              </div>
            ) : (
              <CustomCarousel
                hideButtons
                hideProgress
                selectedIndex={selectedPage}
                items={[
                  <ScreenInfoPage
                    key={"info_page"}
                    screenCreateStatus={screenCreateStatus}
                    screen={props.screen}
                    initialScreenInfo={
                      props.isFirstScreen
                        ? {
                          name: "My First Screen",
                        }
                        : screenInfo
                    }
                    action={
                      !props.screen && props.isFirstScreen && (
                        <Button
                          variant="outline"
                          onClick={() => setShowInitialScreenOptions(true)}
                          className="gap-2"
                        >
                          <MdArrowBack className="size-4" />
                          Back
                        </Button>
                      )
                    }
                    onSubmitted={(val) => {
                      if (props.screen) {
                        updateScreen(val);
                      } else {
                        setScreenInfo(val);
                        setSelectedPage(1);
                      }
                    }}
                  />,
                  <ScreenPairPage
                    key={"pair_page"}
                    onPinChange={setInputPin}
                    screenInfo={screenInfo}
                    onSubmit={onSubmit}
                    screenCreateStatus={screenCreateStatus}
                    setSelectedPage={setSelectedPage}
                    onPairLater={onPairLater}
                    isPairAgain={props.isPairAgain}
                  />,
                ]}
              />
            )}
      </div>
    </div>
  );
}

function UseBrowserButton({ className, onClick }: { className?: string, onClick?: () => void }) {
  return (
    <Tooltip side="bottom" className="max-w-sm" content={
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <FiChrome className="h-6 w-6 text-background" />
          <p className="text-muted text-xs">Opens your content in a new browser tab that acts as a digital display.</p>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 px-2 items-center justify-center rounded-full bg-muted text-foreground text-xs">1</span>
            <p>Click to open a new browser window</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 px-2 items-center justify-center rounded-full bg-muted text-foreground text-xs">2</span>
            <p>Enter the provided PIN to pair your browser</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 px-2 items-center justify-center rounded-full bg-muted text-foreground size-8 text-xs">3</span>
            <p>Your browser is now ready to display content!</p>
          </div>
        </div>
      </div>
    }>
      <Button
        variant="outline"
        size="lg"
        className={className}
        onClick={() => {
          const openUrl = `${env.NEXT_PUBLIC_PLAYER_URL}?source=add_screen&logs=false`
          window.open(openUrl, "_blank");
          onClick?.();
        }}
      >
        <FiChrome className="h-6 w-6" />
        <span>I will use a browser for now</span>
      </Button>
    </Tooltip>
  );
}
